import { useRouter } from 'next/router';
import Script from 'next/script';
import {
  ConsentCategory,
  type TConsentCategory,
} from 'public/assets/js/analytics/utilities';
import { Fragment, useEffect, type FC } from 'react';
import { useTrack } from 'src/contexts';
import { generateUUID, setLiFatId } from 'src/lib/analytics-utils';
import { LINKEDIN_CAPI_EVENTS } from 'src/lib/trackingEvents';

const ThirdPartyScripts: FC<{ enabledCategories: TConsentCategory[] }> = ({
  enabledCategories,
}) => {
  const track = useTrack();
  const { asPath } = useRouter();

  // TODO: this needs to be revisited
  useEffect(() => {
    setLiFatId();
    const uuid = generateUUID();

    track({
      type: LINKEDIN_CAPI_EVENTS.PAGE_VIEW,
      payload: {
        user_data: {
          linkedinFirstPartyId: window.li_fat_id,
        },
        currency: 'USD',
        value: 0,
        event_id: uuid,
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath]);

  return (
    <>
      {enabledCategories.map((category) => {
        switch (category) {
          case ConsentCategory.STRICTLY_NECESSARY:
            return null;

          case ConsentCategory.PERFORMANCE:
            return (
              <Fragment key={category}>
                <Script
                  id='marketo-munchkin-script'
                  dangerouslySetInnerHTML={{
                    __html: `
                  (function (w, d) {
                    const initMunchkin = () => {
                      w?.Munchkin?.init?.('495-WRE-561');
                    };
                    const s = d.createElement('script');
                    s.type = 'text/javascript';
                    s.async = true;
                    s.src = '//munchkin.marketo.net/munchkin.js';
                    s['onreadystatechange'] = function () {
                      if (this.readyState === 'complete' || this.readyState === 'loaded') {
                        initMunchkin();
                      }
                    };
                    s.onload = initMunchkin;
                    d.getElementsByTagName('head')[0].appendChild(s);
                  })(window, document);
                `,
                  }}
                />
                <Script
                  type='text/javascript'
                  src='https://go.plaid.com/rs/495-WRE-561/images/rwts.js'
                />
                <Script
                  id='conversion-ai-script'
                  src='https://p.conversion.ai'
                  strategy='afterInteractive'
                />
                <Script
                  id='conversion-ai-forms-sdk'
                  src='https://forms.conversion.ai/script.js'
                  strategy='afterInteractive'
                />
              </Fragment>
            );

          case ConsentCategory.FUNCTIONAL:
            return null;

          case ConsentCategory.TARGETING:
            return (
              <Fragment key={category}>
                <Script
                  id='qualified-object'
                  dangerouslySetInnerHTML={{
                    __html: `(function(w,q){w['QualifiedObject']=q;w[q]=w[q]||function(){
                      (w[q].q=w[q].q||[]).push(arguments)};})(window,'qualified')`,
                  }}
                />
                <Script
                  id='qualified-dependencies'
                  strategy='lazyOnload'
                  src='https://js.qualified.com/qualified.js?token=D4MCQzekrVDNASqw'
                />
                <Script
                  id='qualified-meetings'
                  dangerouslySetInnerHTML={{
                    __html: `((window) => {
                      const urlParams = new URLSearchParams(window.location.search);
                      const formId = Number(urlParams.get('form'));
                      const qualifiedMeetingForms = { 2006: 'experience-1699026891029', 2007: 'experience-1742313176070', 2008: 'experience-1742313198160' };
                      if (window?.qualified && qualifiedMeetingForms[formId]) {
                        window.qualified('showFormExperience', qualifiedMeetingForms[formId]);
                      }
                    })(window);`,
                  }}
                />
                <Script
                  id='sixsense-client-script'
                  dangerouslySetInnerHTML={{
                    __html: `
                  (function (w,d) {
                    const _6sToken = '84597002790a907dc7593763a409b716';
                    const _6sCompanyDetailsKey = '864b2ce7e4631444c512d49c24ec3a2c11bbf303';
                    /**
                     * enableCompanyDetails caches firmographics in localStorage under
                     * _6senseCompanyDetails, which VWO polls for (up to 5s) to resolve
                     * 6sense audience targeting conditions. Without it VWO segments never match.
                     * The callback is unused because VWO reads the cached key directly.
                     */
                    w._6si = [
                      ['enableEventTracking', true],
                      ['setToken', _6sToken],
                      ['setEndpoint', 'b.6sc.co'],
                      ['setEpsilonKey', _6sCompanyDetailsKey],
                      ['enableCompanyDetails', true, function () {}, 3],
                    ];
                    const gd = d.createElement('script');
                    const s = d.scripts[0];
                    gd.src = '//j.6sc.co/6si.min.js';
                    s.parentNode.insertBefore(gd, s);
                  })(window, document);
                `,
                  }}
                />
                <Script
                  id='ttd-client-script'
                  dangerouslySetInnerHTML={{
                    __html: `
                  (function (d) {
                    const initTTD = () => {
                      if (typeof TTDUniversalPixelApi === 'function') {
                        const universalPixelApi = new TTDUniversalPixelApi();
                        universalPixelApi.init(
                          '4azo4ri',
                          ['u0iai2i'],
                          'https://insight.adsrvr.org/track/up',
                        );
                      }
                    };
                    const gd = document.createElement('script');
                    const s = d.scripts[0];
                    gd.src = '//js.adsrvr.org/up_loader.1.1.0.js';
                    s.parentNode.insertBefore(gd, s);
                    s.onload = initTTD;
                  })(document);
                `,
                  }}
                />
              </Fragment>
            );

          default:
            return null;
        }
      })}
    </>
  );
};

export default ThirdPartyScripts;
